import {
  Breadcrumbs,
  Inline,
  notifySuccess,
  Skeleton,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { useGetContractorInvoicesByIds } from '@omnipresentgroup/domains';
import {
  type ContractorInvoice,
  type ContractorPayment,
  PaymentMethod,
} from '@omnipresentgroup/types';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { ReactComponent as MangopayLogo } from '../../../../assets/mangopay-powered-logo.svg';
import { Loading } from '../../../components';
import { CONTRACTOR_INVOICES_PATH } from '../../../constants';
import { PaymentStep } from '../types';
import { PaymentMethodStep, TransferDetailsStep } from './components';

const CONTRACTOR_INVOICES_KEY = 'contractor-invoices';

export const ContractorInvoicesPayment = () => {
  const navigate = useNavigate();

  const { state } = useLocation();
  const { invoicesIds: stateInvoicesIds } = state;

  const invoicesResults = useGetContractorInvoicesByIds({
    ids: stateInvoicesIds,
    enabled: (stateInvoicesIds as string[])?.length > 0,
  });

  const invoices = useMemo(
    () =>
      (invoicesResults || [])
        .map(({ data }: { data: ContractorInvoice | undefined }) => data)
        .filter(Boolean) as ContractorInvoice[],
    [invoicesResults],
  );

  const isLoading = useMemo(
    () => invoicesResults.some((result) => result.isLoading),
    [invoicesResults],
  );

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    PaymentMethod | undefined
  >(PaymentMethod.MANUAL_BANK_TRANSFER);

  const [currentStep, setCurrentStep] = useState<
    { step: PaymentStep; data?: unknown } | undefined
  >({ step: PaymentStep.PAYMENT_METHOD });

  return (
    <>
      <Stack gap="16">
        <Breadcrumbs
          crumbs={[
            { id: 'home', title: 'Home', onClick: () => navigate('/') },
            {
              id: CONTRACTOR_INVOICES_KEY,
              title: 'Contractor Invoices',
              onClick: () => navigate(CONTRACTOR_INVOICES_PATH),
            },
            {
              id: `${CONTRACTOR_INVOICES_KEY}-bulk-pay`,
              title: 'Pay Invoices',
              active: true,
            },
          ]}
        />
        <Stack gap="0">
          <Inline justify="space-between" minH={40} align="center">
            <Typography as="h2" hideParagraphSpacing size="24" weight="medium">
              {isLoading ? (
                <Skeleton width={170} />
              ) : (
                `Paying ${invoices.length} invoice${invoices.length > 1 ? 's' : ''}`
              )}
            </Typography>
          </Inline>
        </Stack>
      </Stack>
      {isLoading ? (
        <Loading />
      ) : (
        <Stack gap="24" align="center">
          {currentStep?.step === PaymentStep.PAYMENT_METHOD && (
            <PaymentMethodStep
              invoices={invoices}
              selectedPaymentMethod={selectedPaymentMethod}
              onStartPayment={(paymentData: ContractorPayment) => {
                setCurrentStep({
                  step: PaymentStep.TRANSFER_DETAILS,
                  data: paymentData,
                });
              }}
              onClick={setSelectedPaymentMethod}
            />
          )}
          {currentStep?.step === PaymentStep.TRANSFER_DETAILS && invoices && (
            <TransferDetailsStep
              selectedPaymentMethod={selectedPaymentMethod!}
              payment={currentStep.data as ContractorPayment}
              onDone={() => {
                notifySuccess({
                  title: `We're expecting your transfer to arrive soon`,
                });
                navigate(CONTRACTOR_INVOICES_PATH);
              }}
            />
          )}
          <MangopayLogo />
        </Stack>
      )}
    </>
  );
};
